import i18n from "i18next";
import { UserRead } from "client/jspPlatformExperiment";

export const SUPPORTED_LANGUAGES = [
  { code: "en", nativeLabel: "English" },
  { code: "fr", nativeLabel: "Français" },
  { code: "es", nativeLabel: "Español" },
  // "zh" intentionally omitted until the zh locale file is tracked/complete.
] as const;

export type SupportedLanguageCode =
  (typeof SUPPORTED_LANGUAGES)[number]["code"];

export const normalizeLanguage = (
  lng: string | undefined,
): SupportedLanguageCode => {
  const base = (lng ?? "").toLowerCase().split("-")[0];
  const match = SUPPORTED_LANGUAGES.find((language) => language.code === base);
  return match ? match.code : "en";
};

/**
 * Future write-back hook: once the backend stores a per-user language
 * preference, persist the signed-in user's choice here. Must stay
 * best-effort — a failed save must never block the local language change.
 */
const persistLanguagePreference = (_code: SupportedLanguageCode): void => {
  // No-op until the user language preference API exists.
};

/** The single entry point UI components call to change the language. */
export const setUserLanguage = (code: SupportedLanguageCode): void => {
  i18n.changeLanguage(code);
  persistLanguagePreference(code);
};

/**
 * Future read hook: once UserRead carries a language preference, apply it
 * here via i18n.changeLanguage (account preference wins over the
 * device-local choice). Already called from AuthProvider at every point
 * where the user object lands, and from the fetchUserAccount thunk in
 * store/slices/user/userAccountSlice.ts.
 */
export const applyAccountLanguage = (_user: UserRead | undefined): void => {
  // No-op until UserRead carries a language preference field.
};
